// The connectivity heartbeat's refetch decision (pagination audit 2026-10-01).
//
// The heartbeat polls the cheap event-log head (`GET /v1/head`), not the
// full `/v1/state` (LIX + grounding over every material). State is refetched
// only when the head moved since the last tick, so an idle tab costs the
// server one count per tick. The server also pushes "the log moved"
// (`/v1/head/stream`) the moment anything is appended on any machine (an
// assistant's tool, a generation job, another tab or device), and the same
// check runs at once, so a change shows within moments; the heartbeat is the
// fallback when that stream is down.

export type { LogHead };
export { shouldRefetchState, singleFlight };

// The workspace event log's head position, as served by `/v1/head`.
type LogHead = number;

// No previous observation (first tick after mount / reconnect / sign-in)
// counts as changed: something may have moved while this tab wasn't looking.
const shouldRefetchState = (
    previous: LogHead | undefined,
    current: LogHead,
): boolean => previous !== current;

// One check at a time: asked again while one runs, it runs exactly once
// more afterwards, however often it was asked meanwhile (a generation job
// appends in bursts, and each check reads the latest anyway). Bounds the
// reads a burst can cause to two in flight at most, never a queue.
const singleFlight = (run: () => Promise<void>): (() => Promise<void>) => {
    let running = false;
    let again = false;
    return async () => {
        if (running) {
            again = true;
            return;
        }
        running = true;
        try {
            do {
                again = false;
                await run();
            } while (again);
        } finally {
            running = false;
        }
    };
};
